/**
 * DashboardBusqueda.js
 * Modal que muestra el dashboard de búsqueda para mascotas perdidas.
 * Incluye: link público + compartir, mensajes de avistamiento,
 *          placeholder "dónde publicar", editar info de pérdida.
 *
 * Solo visible desde el menú de 3 puntos cuando la mascota está extraviada.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { API_URL } from '../../services/api/config';
import { getAuthHeaders, fetchWrapper } from '../../services/api/utils/fetchWrapper';

export default function DashboardBusqueda({ mascota, onClose, onEditarPerdida }) {
  const [mensajes, setMensajes] = useState([]);
  const [cargandoMensajes, setCargandoMensajes] = useState(true);
  const [copied, setCopied] = useState(false);

  const nombre = mascota?.nombre || 'Mascota';
  const slugOrId = mascota?.perfilPublicoSlug || mascota?.id;
  const publicUrl = `${window.location.origin}/perdido/${slugOrId}`;
  const tienePerfilPublico = mascota?.perfilPublicoBusqueda === true;

  // Calcular días perdido
  const diasPerdido = (() => {
    if (!mascota?.fechaPerdida) return null;
    try {
      const fecha = new Date(mascota.fechaPerdida);
      const hoy = new Date();
      const diff = Math.floor((hoy - fecha) / (1000 * 60 * 60 * 24));
      return diff >= 0 ? diff : null;
    } catch (e) {
      return null;
    }
  })();

  // Cargar mensajes de avistamiento
  const cargarMensajes = useCallback(async () => {
    if (!mascota?.id) return;
    setCargandoMensajes(true);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetchWrapper(
        `${API_URL}/api/objetos/${mascota.id}/mensajes-avistamiento`,
        { headers }
      );
      if (resp.ok) {
        const json = await resp.json();
        setMensajes(json.data || []);
      } else {
        console.error('Error cargando mensajes:', resp.status);
      }
    } catch (err) {
      console.error('Error cargando mensajes:', err);
    } finally {
      setCargandoMensajes(false);
    }
  }, [mascota?.id]);

  useEffect(() => {
    cargarMensajes();
  }, [cargarMensajes]);

  const handleMarcarLeido = async (mensajeId) => {
    try {
      const headers = await getAuthHeaders();
      const resp = await fetchWrapper(
        `${API_URL}/api/objetos/${mascota.id}/mensajes-avistamiento/${mensajeId}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ leido: true }),
        }
      );
      if (resp.ok) {
        setMensajes(prev =>
          prev.map(m => m.id === mensajeId ? { ...m, leido: true } : m)
        );
      } else {
        toast.error('No se pudo marcar como leído');
      }
    } catch (err) {
      toast.error('Error de conexión');
    }
  };

  const handleCopiarLink = () => {
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCompartirWhatsApp = () => {
    const texto = `Ayúdame a encontrar a ${nombre}. Mira su perfil de búsqueda: ${publicUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank');
  };

  const handleCompartirFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}`, '_blank');
  };

  const handleImprimir = () => {
    window.open(`${API_URL}/api/objetos/cartel-pdf/${slugOrId}`, '_blank');
  };

  const formatearFechaRelativa = (fecha) => {
    if (!fecha) return '';
    try {
      const d = fecha instanceof Date ? fecha : new Date(fecha);
      const ahora = new Date();
      const diffMs = ahora - d;
      const diffMin = Math.floor(diffMs / 60000);
      const diffHoras = Math.floor(diffMs / 3600000);
      const diffDias = Math.floor(diffMs / 86400000);

      if (diffMin < 60) return `hace ${diffMin} min`;
      if (diffHoras < 24) return `hace ${diffHoras} hora${diffHoras > 1 ? 's' : ''}`;
      if (diffDias < 7) return `hace ${diffDias} día${diffDias > 1 ? 's' : ''}`;
      return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
    } catch (e) {
      return '';
    }
  };

  const mensajesNoLeidos = mensajes.filter(m => !m.leido).length;

  return (
    <div
      className="modal fade show d-block"
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 'var(--z-modal)',
      }}
    >
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
        <div className="modal-content" style={{ borderRadius: 'var(--radius-xl)' }}>

          {/* Header */}
          <div
            className="modal-header modal-header-gradient d-flex justify-content-between align-items-center"
            style={{
              padding: 'var(--space-4)',
              borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
            }}
          >
            <h5
              className="modal-title mb-0"
              style={{
                color: 'var(--white)',
                fontWeight: 'var(--font-weight-bold)',
              }}
            >
              <i className="bi bi-search me-2"></i>
              Dashboard de Búsqueda - {nombre}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
              aria-label="Cerrar"
            ></button>
          </div>

          {/* Body */}
          <div
            className="modal-body"
            style={{ padding: 'var(--space-4)', maxHeight: '70vh', overflowY: 'auto' }}
          >
            {/* Estado */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-4)',
                padding: 'var(--space-3)',
                backgroundColor: 'var(--gray-50)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--gray-200)',
              }}
            >
              <i className="bi bi-bar-chart-fill" style={{ color: 'var(--feature-primary)', fontSize: 'var(--font-size-lg)' }}></i>
              <div>
                <div style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', color: 'var(--gray-900)' }}>
                  Estado: <span className="text-danger">Perdido</span>
                  {diasPerdido !== null && (
                    <span style={{ color: 'var(--gray-600)', fontWeight: 'var(--font-weight-normal)' }}>
                      {' '}desde hace {diasPerdido} {diasPerdido === 1 ? 'día' : 'días'}
                    </span>
                  )}
                </div>
                {tienePerfilPublico && (
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-success)' }}>
                    <i className="bi bi-globe me-1"></i>Perfil público activo
                  </div>
                )}
                {!tienePerfilPublico && (
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
                    <i className="bi bi-globe me-1"></i>Perfil público no activado
                  </div>
                )}
              </div>
            </div>

            {/* Perfil público + compartir */}
            {tienePerfilPublico && (
              <>
                <SectionHeader icon="bi-link-45deg" title="Perfil público" />
                <div
                  style={{
                    padding: 'var(--space-2) var(--space-3)',
                    backgroundColor: 'var(--gray-50)',
                    border: '1px solid var(--gray-200)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-xs)',
                    wordBreak: 'break-all',
                    color: 'var(--gray-700)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  {publicUrl}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                  <SmallButton onClick={handleCopiarLink} icon={copied ? 'bi-check-lg' : 'bi-link-45deg'} label={copied ? 'Copiado!' : 'Copiar link'} />
                  <SmallButton onClick={handleCompartirWhatsApp} icon="bi-whatsapp" label="WhatsApp" bg="#25D366" color="#fff" />
                  <SmallButton onClick={handleCompartirFacebook} icon="bi-facebook" label="Facebook" bg="#1877F2" color="#fff" />
                </div>
                <div style={{ marginBottom: 'var(--space-4)' }}>
                  <SmallButton onClick={handleImprimir} icon="bi-download" label="Descargar cartel PDF" outline />
                </div>
              </>
            )}

            <Divider />

            {/* Mensajes de avistamiento */}
            <SectionHeader
              icon="bi-chat-left-text-fill"
              title={`Mensajes de avistamiento${mensajesNoLeidos > 0 ? ` (${mensajesNoLeidos} nuevo${mensajesNoLeidos > 1 ? 's' : ''})` : ''}`}
            />

            {cargandoMensajes ? (
              <div style={{ textAlign: 'center', padding: 'var(--space-4)' }}>
                <div className="spinner-border spinner-border-sm" style={{ color: 'var(--feature-primary)' }} role="status" />
                <span style={{ marginLeft: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--gray-500)' }}>
                  Cargando mensajes...
                </span>
              </div>
            ) : mensajes.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: 'var(--space-4)',
                  color: 'var(--gray-500)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                <i className="bi bi-inbox" style={{ fontSize: 'var(--font-size-xl)', display: 'block', marginBottom: 'var(--space-2)' }}></i>
                Aún no hay mensajes de avistamiento.
                {tienePerfilPublico && ' Comparte el perfil público para recibir reportes.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                {mensajes.map((msg) => (
                  <MensajeCard
                    key={msg.id}
                    mensaje={msg}
                    onMarcarLeido={() => handleMarcarLeido(msg.id)}
                    formatearFecha={formatearFechaRelativa}
                  />
                ))}
              </div>
            )}

            <Divider />

            {/* Dónde publicar (placeholder) */}
            <SectionHeader icon="bi-megaphone-fill" title="¿Dónde más publicar? (Próximamente)" />
            <div
              style={{
                padding: 'var(--space-3)',
                backgroundColor: 'var(--gray-50)',
                border: '1px dashed var(--gray-300)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--space-4)',
              }}
            >
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', marginBottom: 'var(--space-2)' }}>
                <i className="bi bi-cone-striped me-1"></i>
                Pronto podrás ver una lista de lugares recomendados en tu zona para difundir:
              </div>
              <ul style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)', paddingLeft: 'var(--space-4)', margin: 0, listStyleType: 'disc' }}>
                <li>Grupos de WhatsApp</li>
                <li>Grupos de Facebook</li>
                <li>Refugios y rescatistas</li>
                <li>Veterinarias</li>
                <li>Páginas de adopción</li>
                <li>Vecinos</li>
              </ul>
            </div>

            <Divider />

            {/* Editar información de pérdida */}
            <SectionHeader icon="bi-pencil-square" title="Editar información de pérdida" />
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', marginBottom: 'var(--space-2)' }}>
              Ubicación, fecha, qué llevaba, dónde buscar, causa del escape.
            </p>
            <button
              className="btn btn-sm btn-outline-secondary transition-base"
              onClick={() => {
                onClose();
                if (onEditarPerdida) onEditarPerdida(mascota);
              }}
            >
              <i className="bi bi-pencil me-1"></i>
              Editar datos de pérdida
            </button>
          </div>

          {/* Footer */}
          <div
            className="modal-footer justify-content-center"
            style={{ padding: 'var(--space-3)' }}
          >
            <button
              type="button"
              className="btn btn-outline-secondary transition-base"
              style={{ fontSize: 'var(--font-size-sm)' }}
              onClick={onClose}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// -- Sub-components --

function SectionHeader({ icon, title }) {
  return (
    <div
      style={{
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--gray-800)',
        marginBottom: 'var(--space-2)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
      }}
    >
      <i className={`bi ${icon}`} style={{ color: 'var(--feature-primary)' }}></i>
      {title}
    </div>
  );
}

function Divider() {
  return (
    <hr style={{ border: 'none', borderTop: '1px solid var(--gray-200)', margin: 'var(--space-3) 0' }} />
  );
}

function SmallButton({ onClick, icon, label, bg, color, outline }) {
  const style = outline
    ? {
        padding: '6px 12px',
        border: '1.5px solid var(--gray-300)',
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'var(--white)',
        color: 'var(--gray-800)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        cursor: 'pointer',
        transition: 'var(--transition-fast)',
      }
    : {
        padding: '6px 12px',
        border: 'none',
        borderRadius: 'var(--radius-md)',
        backgroundColor: bg || 'var(--gray-100)',
        color: color || 'var(--gray-800)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        cursor: 'pointer',
        transition: 'var(--transition-fast)',
      };

  return (
    <button onClick={onClick} style={style}>
      <i className={`bi ${icon} me-1`}></i>
      {label}
    </button>
  );
}

function MensajeCard({ mensaje, onMarcarLeido, formatearFecha }) {
  const esNuevo = !mensaje.leido;

  return (
    <div
      style={{
        padding: 'var(--space-3)',
        backgroundColor: esNuevo ? '#FFF7ED' : 'var(--white)',
        border: `1px solid ${esNuevo ? '#FED7AA' : 'var(--gray-200)'}`,
        borderRadius: 'var(--radius-md)',
        position: 'relative',
      }}
    >
      {/* Header del mensaje */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
          {esNuevo && (
            <span style={{ color: 'var(--color-danger)', fontSize: '8px' }}>
              <i className="bi bi-circle-fill"></i>
            </span>
          )}
          {!esNuevo && (
            <i className="bi bi-check-lg" style={{ color: 'var(--color-success)', fontSize: 'var(--font-size-xs)' }}></i>
          )}
          <span style={{
            fontWeight: 'var(--font-weight-semibold)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--gray-900)',
          }}>
            {mensaje.nombre}
          </span>
        </div>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
          {formatearFecha(mensaje.fechaEnvio)}
          {!esNuevo && ' (leído)'}
        </span>
      </div>

      {/* Cuerpo del mensaje */}
      <p style={{
        fontSize: 'var(--font-size-sm)',
        color: 'var(--gray-700)',
        margin: '0 0 var(--space-2) 0',
        lineHeight: 'var(--line-height-normal)',
        whiteSpace: 'pre-wrap',
      }}>
        "{mensaje.mensaje}"
      </p>

      {/* Teléfono y acciones */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {mensaje.telefono && (
          <a
            href={`tel:${mensaje.telefono}`}
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--feature-primary)',
              textDecoration: 'none',
              fontWeight: 'var(--font-weight-semibold)',
            }}
          >
            <i className="bi bi-telephone me-1"></i>
            {mensaje.telefono}
          </a>
        )}
        {!mensaje.telefono && <span></span>}

        {esNuevo && (
          <button
            onClick={onMarcarLeido}
            style={{
              padding: '2px 8px',
              border: '1px solid var(--gray-300)',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--white)',
              color: 'var(--gray-600)',
              fontSize: 'var(--font-size-xs)',
              cursor: 'pointer',
            }}
          >
            <i className="bi bi-check2 me-1"></i>Marcar como leído
          </button>
        )}
      </div>
    </div>
  );
}
